import { useEffect, useRef } from 'react';
import { HOT_EDIT_DESIGN_SYSTEM_EVENT } from '@ideoz/workbench';
import { projectService } from '@ideoz/core';

/**
 * The founder's design system changes, written where every generation reads them.
 *
 * When the editor's question is answered "Everywhere", Apply writes the token into this
 * prototype's `src/index.css`. That alone does not last: an edit turn seeds the theme files from
 * the spec, and the spec is built from the startup's design system, so the colour came back to
 * what it was after a turn or two (test record, D5).
 *
 * So the same change is sent to `PATCH /projects/:id/design-system`. The AI API builds every
 * spec's theme from that row (`enrich_spec_from_design_system`), and Chef seeds `index.css` from
 * the spec - so from here on the founder's colour is what new prototypes are built with, and no
 * turn can take it back.
 *
 * Failures are logged, never surfaced: the change is already in the prototype the founder is
 * looking at, and a failed write here is not something they can act on.
 */
export function useDesignSystemFromEditor(projectId: string | null | undefined): void {
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  useEffect(() => {
    const onChange = (event: Event): void => {
      const patch = (event as CustomEvent<{ patch?: Record<string, string> }>).detail?.patch;
      const id = projectIdRef.current;
      if (!patch || !id) return;
      void projectService.updateDesignSystem(id, patch).catch((error: unknown) => {
        console.warn('[Prototyper] the design system did not take the editor change', {
          fields: Object.keys(patch),
          error,
        });
      });
    };
    window.addEventListener(HOT_EDIT_DESIGN_SYSTEM_EVENT, onChange);
    return () => window.removeEventListener(HOT_EDIT_DESIGN_SYSTEM_EVENT, onChange);
  }, []);
}
